/**
 * useAskQuery — the SSE consumer for the dashboard "Ask" feature (PR3, D2) + durable chat persistence.
 *
 * This is the SPA's FIRST streaming code. It is a streaming mutation, not React Query — RQ buys nothing
 * for a one-shot token stream, and the answer lands as local state that grows token by token.
 *
 * The parsing/consumption is factored into two PURE, exported cores (parseSSEChunk, consumeQueryStream) so
 * they can be unit-tested without a DOM or a live backend — the chunk-boundary bug and the error-frame
 * handling are exactly the things a "looks fine in a short test" implementation gets wrong.
 *
 * PERSISTENCE (migration 134): the `turns` used to live ONLY in React state, so a reload or leaving the
 * Knowledge surface wiped the conversation. Now each COMPLETED turn is saved server-side
 * (POST /api/surfaces/query/chats), the most-recent thread is HYDRATED on mount, and a chat list lets the
 * user switch/start/delete threads. Persistence is BEST-EFFORT and never blocks or breaks the live chat: a
 * failed save only means that turn won't survive a reload (the on-screen answer is unaffected). It is
 * DISTINCT from the engine's query_exchanges Q&A memory — this is the raw resumable transcript.
 *
 * Wire format (see @terum/contracts query.ts): `data: {json}\n\n`, `content-type: text/event-stream`.
 * Frame order: token* → sources → done. An `error` frame is TERMINAL and is a real failed turn — it must
 * never render as an empty answer bubble (the false-empty the engine refuses to produce, bug-424).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch, apiStreamFetch, ApiError, isNotFound } from '../lib/api';
import { QueryStreamEventSchema, type QuerySource, type QueryStreamEvent } from '@terum/contracts';

export type AskStatus = 'streaming' | 'done' | 'error';

export interface AskTurn {
  question: string;
  answer: string;
  sources: QuerySource[];
  status: AskStatus;
  error?: string;
  // What the engine is doing right now, already phrased for display. Live only: it is set from `progress`
  // frames and CLEARED the instant the first answer token arrives, so a settled turn never carries one and
  // a restored turn never has one. Null/absent means "show nothing", not "show a generic spinner".
  progress?: string | null;
  // The skills the engine ACTIVATED this turn (stage:"skill" frames — skills S1, Teddy directive
  // 2026-08-12: activation is visible, never silent). ACCUMULATED, not replaced: the names annotate the
  // thinking indicator for the line's whole lifetime (search + pre-token synthesis), rather than
  // flashing for one frame-interval like a sub-query label. Live only; cleared at every terminal.
  skills?: string[] | null;
}

// A saved chat thread in the list rail (server shape — GET /api/surfaces/query/chats).
export interface ChatSummary {
  id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
}
// One persisted turn as loaded from the server (GET /api/surfaces/query/chats/:id).
interface StoredTurn {
  id: string;
  question: string;
  answer: string;
  sources: QuerySource[];
  created_at: string;
}

export interface UseAskQuery {
  turns: AskTurn[];
  ask: (question: string) => void;
  regenerate: () => void;
  cancel: () => void;
  streaming: boolean;
  // Persistence surface.
  chats: ChatSummary[];
  activeChatId: string | null;
  hydrated: boolean; // the initial load finished (an empty thread after hydration is genuinely empty)
  // TRUE only while a loadChat fetch is actually in flight. The surface shows "Loading…" from THIS, never
  // from an activeChatId-vs-URL mismatch — a mismatch also happens for a brand-new local thread (asked
  // before the server mints its id) and for a deleted/failed thread, and inferring "loading" from it masks
  // the live answer behind a permanent spinner.
  loadingChat: boolean;
  // A deep-linked thread (/ask/:id) that 404s — either purged or never the caller's. Drives the surface's
  // "not on your account" state instead of a blank thread. Cleared the moment a real thread loads or a new
  // one starts. Only 'not_found' today; a transient load failure leaves the current thread untouched.
  loadError: 'not_found' | null;
  newChat: () => void;
  loadChat: (id: string) => void;
  // Adopt a chat id minted OUTSIDE the persist path — specifically the uploads route's mint-on-attach
  // (U6), where dropping a file into a bare thread creates the chat before the first answer does.
  // Thread-generation-safe: a stale adopt (the user switched threads meanwhile) is ignored, exactly as a
  // late persist is. Making activeChatId flip is what drives AskRoute to navigate to /ask/<chatId>.
  adoptChat: (id: string) => void;
  // REJECTS when the server refuses the delete — the caller must handle it (AskSurface reports that
  // the thread is still there). Declared `void`, this contract said the opposite of what the
  // implementation does, so the next caller would have written an unhandled rejection.
  deleteChat: (id: string) => Promise<void>;
}

/**
 * Parse the SSE frames buffered so far. Returns the complete events and the LEFTOVER partial frame, which
 * the caller must prepend to the next chunk. A `data:` frame is routinely split across two `reader.read()`
 * chunks, so splitting on `\n\n` and dropping the trailing partial would intermittently swallow tokens.
 * A malformed or non-`data:` line is skipped, never thrown on.
 */
export function parseSSEChunk(buffer: string): { events: QueryStreamEvent[]; rest: string } {
  const parts = buffer.split('\n\n');
  const rest = parts.pop() ?? ''; // the last piece has no terminating \n\n yet — keep it for next time
  const events: QueryStreamEvent[] = [];
  for (const part of parts) {
    const line = part.trim();
    if (!line.startsWith('data:')) continue;
    const payload = line.slice(line.indexOf(':') + 1).trim();
    if (!payload) continue;
    let raw: unknown;
    try {
      raw = JSON.parse(payload);
    } catch {
      continue; // a corrupt frame is skipped, not fatal
    }
    const ev = QueryStreamEventSchema.safeParse(raw);
    if (ev.success) events.push(ev.data);
  }
  return { events, rest };
}

export interface StreamHandlers {
  onToken: (delta: string) => void;
  onSources: (sources: QuerySource[]) => void;
  // `accessLogId` is the server-minted access-record id off the `done` frame (null when the server didn't
  // mint one — flag off, write failed, or an older server). The hook echoes it back on the transcript
  // POST (the D31 linkage echo); it is never rendered.
  onDone: (accessLogId: string | null) => void;
  onError: (message: string) => void;
  // Advisory, non-terminal. A stream may deliver none of these (an older server, or a cache hit that
  // answers instantly), so nothing downstream may treat a progress frame as required.
  onProgress: (label: string) => void;
  // A skill activation (stage:"skill" — the frame's `detail` is the skill name). Advisory like
  // onProgress; a server without the skills layer sends none.
  onSkill: (name: string) => void;
}

// Turn a `progress` frame into the line the user reads. Lives here rather than in the surface so the
// display string is derived once from the contract, not re-invented per consumer.
//
// The sub-query is shown in full because it is the interesting part: it makes the engine's decomposition
// of YOUR question visible, which is what turns a dead wait into evidence that something specific is
// happening. The server already bounds its length.
function progressLabel(ev: { stage: 'searching' | 'reading' | 'skill'; detail?: string | null; count?: number | null }): string {
  if (ev.stage === 'reading') {
    const n = ev.count ?? 0;
    return `Reading ${n} ${n === 1 ? 'note' : 'notes'}`;
  }
  return ev.detail ? `Searching: “${ev.detail}”` : "Searching your team's knowledge";
}

/**
 * Drive an SSE reader to completion, invoking a handler per frame. Returns `{ terminal }` — true if a
 * `done` or `error` frame arrived. A false `terminal` means the stream closed mid-answer (truncated); the
 * caller decides how to present that. TextDecoder runs in `stream: true` mode so a multi-byte character
 * that straddles a chunk is not corrupted. Testable with a fake reader; used by the hook with a real one.
 */
export async function consumeQueryStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  handlers: StreamHandlers,
): Promise<{ terminal: boolean }> {
  const decoder = new TextDecoder();
  let buffer = '';
  let terminal = false;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const { events, rest } = parseSSEChunk(buffer);
    buffer = rest;
    for (const ev of events) {
      if (ev.type === 'token') handlers.onToken(ev.value);
      else if (ev.type === 'progress') {
        // A skill activation is NOT a replaceable status label — it accumulates on the turn (the
        // indicator annotates with every active skill), so it takes its own handler. The frame's
        // `detail` carries the name; a nameless skill frame says nothing and is dropped.
        if (ev.stage === 'skill') {
          if (ev.detail) handlers.onSkill(ev.detail);
        } else handlers.onProgress(progressLabel(ev));
      } else if (ev.type === 'sources') handlers.onSources(ev.value);
      else if (ev.type === 'done') {
        terminal = true;
        handlers.onDone(ev.accessLogId ?? null);
      } else if (ev.type === 'error') {
        terminal = true;
        handlers.onError(ev.message);
      }
    }
  }
  return { terminal };
}

function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { error?: string } | undefined;
    return body?.error || e.message; // the route sends {error} on 400/429/500 — prefer it over the status line
  }
  return e instanceof Error ? e.message : 'The request failed.';
}

const CHATS_PATH = '/api/surfaces/query/chats';

export function useAskQuery(): UseAskQuery {
  const [turns, setTurns] = useState<AskTurn[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [chats, setChats] = useState<ChatSummary[]>([]);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [loadingChat, setLoadingChat] = useState(false);
  const [loadError, setLoadError] = useState<'not_found' | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // A live mirror of `turns`, so `ask` can read prior turns for `history` and the new turn's index without
  // depending on a stale closure.
  const turnsRef = useRef<AskTurn[]>([]);
  useEffect(() => {
    turnsRef.current = turns;
  }, [turns]);
  // Live mirror of the active chat id (so a persist that CREATES the chat can hand the id to the next one
  // without waiting for a re-render).
  const activeChatIdRef = useRef<string | null>(null);
  // turn index → its server-side turn id, so a regenerate UPDATES its row instead of appending a duplicate.
  const serverTurnIdsRef = useRef<Map<number, string>>(new Map());
  // Persists run one-at-a-time in enqueue order. This is what makes chat CREATION safe: two quick turns
  // can't each create a chat — the first completes (and sets activeChatIdRef) before the second runs.
  const persistQueueRef = useRef<Promise<void>>(Promise.resolve());
  // Bumped every time the ACTIVE thread changes (newChat/loadChat/hydrate/delete-active). A queued persist
  // captures the generation it belongs to; if the user has since switched threads, the save targets its
  // ORIGINAL chat by captured id (never the switched-in thread) and never clobbers the new active refs.
  const threadGenRef = useRef(0);

  // Abort a stream in flight if the component unmounts (navigating away from Ask) — an abandoned reader
  // that keeps writing into a dead component is a leak, and a quick answer runs ~20-45s.
  useEffect(() => () => abortRef.current?.abort(), []);

  const patchTurn = useCallback((idx: number, patch: Partial<AskTurn>) => {
    setTurns((prev) => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  }, []);

  const refreshChats = useCallback(async () => {
    try {
      const { chats } = await apiFetch<{ chats: ChatSummary[] }>(CHATS_PATH);
      setChats(chats ?? []);
    } catch {
      // Best-effort: a failed list refresh just leaves the rail stale until the next one.
    }
  }, []);

  // Persist one completed turn (create-or-append-or-update), serialized through persistQueueRef. Best-effort.
  //
  // The active-thread refs are read LATE (inside the queued job) so the first turn of a NEW chat picks up
  // the id the create-POST just minted — the create→append chain. But the user can switch threads mid-save
  // (newChat/loadChat/deleteChat mutate those refs via resetThread), so we ALSO snapshot the thread
  // generation + the chat/turn ids at enqueue: if the thread changed by the time the job runs, we save to
  // the ORIGINAL chat by its captured id (never the switched-in one), and never adopt the minted id into the
  // now-different active thread. A brand-new thread abandoned before it was ever saved (no captured id) is
  // dropped — there is nothing to update, and creating it would resurrect an abandoned chat.
  const persistTurn = useCallback(
    (idx: number, question: string, answer: string, sources: QuerySource[], accessLogId: string | null = null) => {
      const gen = threadGenRef.current;
      const capturedChatId = activeChatIdRef.current;
      const capturedTurnId = serverTurnIdsRef.current.get(idx) ?? null;
      persistQueueRef.current = persistQueueRef.current.then(async () => {
        const onThread = threadGenRef.current === gen;
        // On-thread → read late (create-chain). Off-thread → the original chat's captured id.
        const chatId = onThread ? activeChatIdRef.current : capturedChatId;
        const turnId = onThread ? serverTurnIdsRef.current.get(idx) ?? null : capturedTurnId;
        if (!onThread && !chatId) return; // abandoned, never-saved new thread → nothing to persist
        try {
          // `accessLogId` is the D31 linkage echo: the server-minted access-record id off this turn's
          // `done` frame, returned verbatim. The server verifies ownership before storing it, so an
          // absent/stale echo degrades to an unlinked turn (display-JSONB re-gate), never a failed save.
          const res = await apiFetch<{ chatId: string; turnId: string }>(CHATS_PATH, {
            method: 'POST',
            body: JSON.stringify({ chatId, turnId, question, answer, sources, accessLogId }),
          });
          // Adopt the minted ids into the ACTIVE thread ONLY if the user is still on it (re-checked after
          // the await) — otherwise we'd clobber the thread they switched to.
          if (threadGenRef.current === gen) {
            activeChatIdRef.current = res.chatId;
            setActiveChatId(res.chatId);
            serverTurnIdsRef.current.set(idx, res.turnId);
          }
          await refreshChats(); // title/order changed
        } catch {
          // A failed save must NOT break the live chat — the turn is already on screen; it just won't
          // survive a reload. (Expected before migration 134 is applied — the route 500s on a missing table.)
        }
      });
    },
    [refreshChats],
  );

  const runTurn = useCallback(
    async (question: string, history: { question: string; answer: string }[], controller: AbortController, idx: number) => {
      let answer = '';
      let sources: QuerySource[] = [];
      let accessLogId: string | null = null;
      let terminalKind: 'done' | 'error' | null = null;
      try {
        const res = await apiStreamFetch('/api/surfaces/query', {
          method: 'POST',
          // `chatId` (U6) is read LATE off the ref — a file attached before the first question mints the
          // chat via the uploads route and adopts it here, so by the time this fires the id exists and the
          // engine's chat-scoped searchAttachments tool can read the attachment. Null on a brand-new
          // thread with no attachment — byte-identical to pre-U6.
          body: JSON.stringify({ question, history, chatId: activeChatIdRef.current }),
          signal: controller.signal,
        });
        const reader = res.body?.getReader();
        if (!reader) throw new Error('The response had no body.');
        const skillNames: string[] = [];
        const { terminal } = await consumeQueryStream(reader, {
          onProgress: (label) => {
            // Only while the bubble is still empty. Once prose is on screen a status line underneath it is
            // noise, and a late frame must not resurrect one.
            if (!answer) patchTurn(idx, { progress: label });
          },
          onSkill: (name) => {
            // ACCUMULATE (deduped) — the indicator shows every skill active this turn for the line's
            // whole lifetime, not a one-frame flash (skills S1 directive). Same bubble-empty guard as
            // onProgress: once prose streams, the line is gone and a late frame must not resurrect it.
            if (!answer && !skillNames.includes(name)) {
              skillNames.push(name);
              patchTurn(idx, { skills: [...skillNames] });
            }
          },
          onToken: (delta) => {
            const first = !answer;
            answer += delta;
            // Clearing on the FIRST token is what makes the handoff read as one motion: the status line is
            // replaced by the answer rather than lingering beside it.
            patchTurn(idx, first ? { answer, progress: null } : { answer });
          },
          onSources: (s) => {
            sources = s;
            patchTurn(idx, { sources: s });
          },
          onDone: (id) => {
            terminalKind = 'done';
            accessLogId = id;
            patchTurn(idx, { status: 'done', progress: null, skills: null });
          },
          onError: (message) => {
            terminalKind = 'error';
            patchTurn(idx, { status: 'error', error: message, progress: null, skills: null });
          },
        });
        // Stream closed with no done/error frame → the answer was cut off mid-flight.
        // EVERY terminal path clears `progress`: a turn that ends before its first token (an error frame,
        // or a search that failed) would otherwise sit under a permanent "Searching…" line.
        if (!terminal) {
          patchTurn(
            idx,
            answer
              ? { status: 'done', progress: null, skills: null }
              : { status: 'error', error: 'The answer ended unexpectedly.', progress: null, skills: null },
          );
        }
        // Persist a genuine completion only: a non-empty answer that did not end in an error frame. A
        // truncated-but-has-content answer counts; a user-aborted turn throws below and is skipped.
        if (terminalKind !== 'error' && answer.trim()) {
          persistTurn(idx, question, answer, sources, accessLogId);
        }
      } catch (e) {
        // An aborted stream is a user action (cancel, or a new question replacing this one), not a failure:
        // keep whatever streamed so far as a completed turn, but do NOT persist a half-abandoned answer.
        if (controller.signal.aborted) {
          patchTurn(idx, { status: 'done', progress: null, skills: null });
          return;
        }
        patchTurn(idx, { status: 'error', error: errorMessage(e), progress: null, skills: null });
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
          setStreaming(false);
        }
      }
    },
    [patchTurn, persistTurn],
  );

  const ask = useCallback(
    (question: string) => {
      const q = question.trim();
      if (!q) return;
      setLoadError(null); // asking from the not-found state starts a fresh, real thread
      abortRef.current?.abort(); // a new question replaces any stream still in flight
      const controller = new AbortController();
      abortRef.current = controller;
      // history = the prior COMPLETED turns; a streaming/errored turn is not a real prior answer.
      const history = turnsRef.current
        .filter((t) => t.status === 'done' && t.answer)
        .map((t) => ({ question: t.question, answer: t.answer }));
      const idx = turnsRef.current.length; // the index the appended turn will occupy
      setTurns((prev) => [...prev, { question: q, answer: '', sources: [], status: 'streaming' }]);
      setStreaming(true);
      void runTurn(q, history, controller, idx);
    },
    [runTurn],
  );

  // Re-run the LAST turn's question, replacing that turn IN PLACE (standard "regenerate"). Unlike ask(),
  // it does not append — so the thread never accumulates duplicate questions and `history` stays correct
  // (the regenerated turn is excluded from its own history). No-op while that turn is still streaming.
  const regenerate = useCallback(() => {
    const prev = turnsRef.current;
    if (prev.length === 0) return;
    const idx = prev.length - 1;
    const last = prev[idx];
    if (last.status === 'streaming') return;
    abortRef.current?.abort(); // in case another stream is somehow in flight
    const controller = new AbortController();
    abortRef.current = controller;
    const history = prev
      .slice(0, idx)
      .filter((t) => t.status === 'done' && t.answer)
      .map((t) => ({ question: t.question, answer: t.answer }));
    setTurns((cur) =>
      cur.map((t, i) => (i === idx ? { question: last.question, answer: '', sources: [], status: 'streaming' } : t)),
    );
    setStreaming(true);
    void runTurn(last.question, history, controller, idx);
  }, [runTurn]);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  // Reset local + ref state to an empty thread. Shared by newChat and a switch/hydrate.
  const resetThread = useCallback((next: AskTurn[], chatId: string | null, turnIds: Map<number, string>) => {
    abortRef.current?.abort();
    threadGenRef.current += 1; // the active thread is changing — in-flight persists must not target it
    setStreaming(false);
    setLoadError(null); // a real thread (loaded) or an empty one (newChat) clears any prior not-found
    setTurns(next);
    turnsRef.current = next;
    serverTurnIdsRef.current = turnIds;
    activeChatIdRef.current = chatId;
    setActiveChatId(chatId);
  }, []);

  const applyThread = useCallback(
    (thread: { id: string; turns: StoredTurn[] }) => {
      const loaded: AskTurn[] = thread.turns.map((t) => ({
        question: t.question,
        answer: t.answer,
        sources: t.sources ?? [],
        status: 'done' as const,
      }));
      resetThread(loaded, thread.id, new Map(thread.turns.map((t, i) => [i, t.id])));
    },
    [resetThread],
  );

  const newChat = useCallback(() => {
    resetThread([], null, new Map());
  }, [resetThread]);

  // Adopt an externally-minted chat id (uploads mint-on-attach, U6) into the ACTIVE thread. Mint-on-attach
  // only fires when there was NO active chat, so the ONLY safe state to adopt into is the still-bare
  // thread: if the user switched to a REAL thread mid-upload (activeChatId is now some OTHER id), adopting
  // would clobber their place and navigate them away — so a non-null, non-matching active id is refused.
  // (The uploads hook applies the matching generation guard on the chip side.) A no-op when the id is
  // already active (an attach that already had a chatId).
  const adoptChat = useCallback((id: string) => {
    if (activeChatIdRef.current === id) return;
    if (activeChatIdRef.current !== null) return; // switched to a real thread mid-upload — do not clobber it
    activeChatIdRef.current = id;
    setActiveChatId(id);
  }, []);

  const loadChat = useCallback(
    (id: string) => {
      void (async () => {
        setLoadingChat(true);
        try {
          const thread = await apiFetch<{ id: string; title: string | null; turns: StoredTurn[] }>(`${CHATS_PATH}/${id}`);
          applyThread(thread); // resetThread clears loadError on a real load
        } catch (e) {
          // A 404 is a thread that isn't the caller's (or was purged) → the surface shows a "not on your
          // account" state instead of a blank thread. A transient failure leaves the current (empty) thread
          // in place — the surface falls back to a usable composer rather than a stuck spinner.
          if (isNotFound(e)) setLoadError('not_found');
        } finally {
          setLoadingChat(false); // ALWAYS clear — a stuck true is a permanent "Loading…"
        }
      })();
    },
    [applyThread],
  );

  // Rejects if the server refused the delete, instead of swallowing it into a bare `catch { return }`.
  // The row correctly stayed in the list on failure, but the caller had no way to know: the ✕ simply
  // did nothing, and on the ACTIVE thread the surface had already navigated away by then — so a
  // failed delete looked like a successful one that also lost your place.
  const deleteChat = useCallback(
    async (id: string) => {
      await apiFetch(`${CHATS_PATH}/${id}`, { method: 'DELETE' });
      setChats((prev) => prev.filter((c) => c.id !== id));
      if (activeChatIdRef.current === id) newChat();
    },
    [newChat],
  );

  // HYDRATE on mount: load the chat list for the thread rail. It does NOT auto-resume the most-recent
  // thread — as a peer surface (D-ask-peer), bare /ask is an empty new question and the rail carries
  // history; a specific thread is resumed only via /ask/:chatId, which AskRoute drives through loadChat.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { chats } = await apiFetch<{ chats: ChatSummary[] }>(CHATS_PATH);
        if (cancelled) return;
        setChats(chats ?? []);
      } catch {
        // Best-effort — a failed list load just leaves the rail empty.
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { turns, ask, regenerate, cancel, streaming, chats, activeChatId, hydrated, loadingChat, loadError, newChat, loadChat, adoptChat, deleteChat };
}
